import React, { useRef, useState, useEffect } from "react";
import { useWS } from "@/context/WebSocketContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Smile, Paperclip, SendHorizonal, X, Loader2, CornerUpLeft, Pencil, Check } from "lucide-react";
import { toast } from "sonner";

const EMOJIS = ["😀","😁","😂","🤣","😊","😍","😘","😎","🤔","😢","😭","😅","👍","👎","👏","🙏","🔥","❤️","🎉","✅","❌","⚡","💡","📎","📌","⭐","😴","🤝","💪","🚀"];

export default function Composer({ conversationId, onSend, replyTarget, onCancelReply, editTarget, onSubmitEdit, onCancelEdit }) {
  const { sendTyping } = useWS();
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const taRef = useRef(null);
  const typingRef = useRef(false);
  const typingTimer = useRef(null);

  useEffect(() => { if (replyTarget) taRef.current?.focus(); }, [replyTarget]);

  useEffect(() => {
    if (editTarget) {
      setText(editTarget.text || "");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      taRef.current?.focus();
    }
  }, [editTarget]);

  const cancelEdit = () => { setText(""); onCancelEdit?.(); };

  useEffect(() => () => { clearTimeout(typingTimer.current); if (typingRef.current) sendTyping(conversationId, false); }, [conversationId, sendTyping]);

  const onChange = (e) => {
    setText(e.target.value);
    if (editTarget) return;
    if (!typingRef.current) { typingRef.current = true; sendTyping(conversationId, true); }
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => { typingRef.current = false; sendTyping(conversationId, false); }, 2500);
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10 MB)"); return; }
    setFile(f);
  };

  const submit = async () => {
    if (editTarget) {
      const t = text.trim();
      if (!t) return;
      setSending(true);
      try {
        await onSubmitEdit(editTarget.id, t);
        setText("");
        onCancelEdit?.();
        taRef.current?.focus();
      } catch (e) { /* handled upstream */ } finally { setSending(false); }
      return;
    }
    if (!text.trim() && !file) return;
    setSending(true);
    clearTimeout(typingTimer.current);
    typingRef.current = false;
    sendTyping(conversationId, false);
    try {
      await onSend(text.trim(), file, replyTarget?.id || null);
      setText("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onCancelReply?.();
      taRef.current?.focus();
    } catch (e) {
      /* handled upstream */
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  return (
    <div className="border-t border-border bg-background/95 backdrop-blur-md p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      {editTarget && (
        <div className="flex items-center gap-2 mb-2 text-xs bg-muted rounded-lg px-3 py-2 border-l-4 border-amber-500" data-testid="edit-preview">
          <Pencil className="h-4 w-4 text-amber-500 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-amber-600 dark:text-amber-400">Modifica nota</div>
            <div className="truncate text-muted-foreground">{editTarget.text || ""}</div>
          </div>
          <button onClick={cancelEdit} className="hover:text-destructive flex-shrink-0" data-testid="cancel-edit"><X className="h-4 w-4" /></button>
        </div>
      )}
      {!editTarget && replyTarget && (
        <div className="flex items-center gap-2 mb-2 text-xs bg-muted rounded-lg px-3 py-2 border-l-4 border-sky-500" data-testid="reply-preview">
          <CornerUpLeft className="h-4 w-4 text-sky-500 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sky-600 dark:text-sky-400 truncate">{replyTarget.senderName}</div>
            <div className="truncate text-muted-foreground">{replyTarget.text || "📎 Allegato"}</div>
          </div>
          <button onClick={onCancelReply} className="hover:text-destructive flex-shrink-0" data-testid="cancel-reply"><X className="h-4 w-4" /></button>
        </div>
      )}
      {!editTarget && file && (
        <div className="flex items-center gap-2 mb-2 text-xs bg-muted rounded-lg px-3 py-2" data-testid="attachment-preview">
          <Paperclip className="h-4 w-4" />
          <span className="truncate flex-1">{file.name}</span>
          <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="hover:text-destructive"><X className="h-4 w-4" /></button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="flex-shrink-0" data-testid="emoji-button"><Smile className="h-5 w-5" /></Button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="w-64 p-2">
            <div className="grid grid-cols-6 gap-1">
              {EMOJIS.map((em) => (
                <button key={em} className="text-xl hover:bg-muted rounded p-1" onClick={() => setText((t) => t + em)}>{em}</button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {!editTarget && (
          <>
            <Button variant="ghost" size="icon" className="flex-shrink-0" onClick={() => fileRef.current?.click()} data-testid="attach-button">
              <Paperclip className="h-5 w-5" />
            </Button>
            <input type="file" ref={fileRef} onChange={pickFile} className="hidden" data-testid="file-input" />
          </>
        )}

        <textarea
          ref={taRef}
          data-testid="message-input"
          rows={1}
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={editTarget ? "Modifica la nota..." : "Aggiungi una nota..."}
          className="flex-1 resize-none max-h-32 rounded-2xl border border-input bg-muted/50 px-4 py-2.5 text-base focus:outline-none focus:ring-1 focus:ring-ring"
        />

        <Button size="icon" onClick={submit} disabled={sending || (!editTarget && !text.trim() && !file) || (editTarget && !text.trim())} data-testid="send-message-button"
          className="flex-shrink-0 rounded-full bg-sky-600 hover:bg-sky-700 text-white h-10 w-10">
          {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : (editTarget ? <Check className="h-5 w-5" /> : <SendHorizonal className="h-5 w-5" />)}
        </Button>
      </div>
    </div>
  );
}
